

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Skeleton,
    Snackbar,
    Alert,
    IconButton,
} from "@mui/material";
import { Edit, Delete, Save, Close } from '@mui/icons-material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { useAuth } from 'auth/Auth';
import InlineEditField from 'components/CustonEditableInlineField/CustomEditableInlineField';
import { formatDate } from 'utils/DateTimeConversion';
import { commonIconButtonStyles, commonIconStyles } from 'assets/theme/components/iconButtonStyles';
import { UiDetailSection, UiLabeledValue } from 'components/v2/record/DetailSection';

const TripComponentDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { axiosInstance } = useAuth();
    const [component, setComponent] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [updatedFields, setUpdatedFields] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        fetchData();
    }, [id]);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get(`/trip-components/${id}`);
            setComponent(response.data);
        } catch (error) {
            console.error('Error fetching Trip Logistics details:', error);
            setSnackbar({ open: true, message: 'Error loading Trip Logistics details', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleFieldUpdate = (field, value) => {
        setUpdatedFields(prev => ({ ...prev, [field]: value }));
        setComponent(prev => ({ ...prev, [field]: value }));
    };

    const handleSaveChanges = async () => {
        if (Object.keys(updatedFields).length === 0) {
            setIsEditing(false);
            return;
        }
        try {
            await axiosInstance.put(`/trip-components/${id}`, updatedFields);
            setUpdatedFields({});
            setIsEditing(false);
            setSnackbar({ open: true, message: 'Trip Logistics updated successfully', severity: 'success' });
            fetchData();
        } catch (error) {
            console.error('Error updating Trip Logistics:', error);
            setSnackbar({ open: true, message: 'Error updating Trip Logistics', severity: 'error' });
        }
    };

    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this Trip Logistics?')) {
            try {
                await axiosInstance.delete(`/trip-components/${id}`);
                setSnackbar({ open: true, message: 'Trip Logistics deleted successfully', severity: 'success' });
                setTimeout(() => navigate('/trip-components'), 1000);
            } catch (error) {
                console.error('Error deleting Trip Logistics:', error);
                setSnackbar({ open: true, message: 'Error deleting Trip Logistics', severity: 'error' });
            }
        }
    };

    const handleUserClick = (userId, userName) => {
        if (userId) {
            navigate(`/users/${userId}/profile`);
        } else {
            setSnackbar({ open: true, message: `Unable to find user info for ${userName}.`, severity: 'info' });
        }
    };

    const renderSection = (title, fields) => {
      // One body for both UIs: v2 shows it in a DetailSection, legacy keeps its card.
      const sectionBody = (
        <>
            <Grid container spacing={3}>
                {fields.map((item) => (
                    <Grid item xs={12} md={item.fullWidth ? 12 : 6} key={item.field}>
                        {['created_by_name', 'updated_by_name'].includes(item.field) ? (
                            <UiLabeledValue label={item.label}>
                            <MDBox>
                                <MDTypography variant="button" fontWeight="regular" color="text">
                                    <strong>{item.label}:</strong>
                                </MDTypography>
                                <MDTypography
                                    component="span"
                                    onClick={() => handleUserClick(item.field === 'created_by_name' ? component.created_by : component.updated_by, component[item.field])}
                                    variant="button"
                                    color="info"
                                    fontWeight="medium"
                                    sx={{ cursor: 'pointer', ml: 1 }}
                                >
                                    {component[item.field]}
                                </MDTypography>
                            </MDBox>
                            </UiLabeledValue>
                        ) : (
                            <InlineEditField
                                label={item.label}
                                value={component?.[item.field]}
                                onSave={(newValue) => handleFieldUpdate(item.field, newValue)}
                                isEditing={isEditing}
                                readOnly={item.readOnly || false}
                                renderValue={item.renderValue}
                                multiline={item.multiline}
                                type={item.type}
                                options={item.options}
                            />
                        )}
                    </Grid>
                ))}
            </Grid>
        </>
      );
      return (
        <UiDetailSection
          title={title}
          legacy={
            <MDBox mb={3} p={3} borderRadius="lg" sx={{ backgroundColor: '#ffffff', boxShadow: 3 }}>
                <MDTypography variant="h6" fontWeight="medium" mb={2}>{title}</MDTypography>
                {sectionBody}
            </MDBox>
          }
        >
          {sectionBody}
        </UiDetailSection>
      );
    };

    if (isLoading) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox p={3}><Skeleton variant="rectangular" height={400} /></MDBox>
                
            </DashboardLayout>
        );
    }

    if (!component) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox p={3}><MDTypography>Trip Logistics not found</MDTypography></MDBox>
                
            </DashboardLayout>
        );
    }

    const categoryOptions = [
        { value: 'Accommodation', label: 'Accommodation' },
        { value: 'Meal', label: 'Meal' },
        { value: 'Transportation', label: 'Transportation' },
        { value: 'Activity', label: 'Activity' },
        { value: 'Misc', label: 'Misc' }
    ];

    const accomSubCategories = [
        { value: 'shared dorms', label: 'Shared Dorms' },
        { value: 'basic guesthouses + twinshare', label: 'Basic Guesthouses + Twinshare' },
        { value: 'twinshare hotels', label: 'Twinshare Hotels' },
        { value: 'expedition travellers', label: 'Expedition Travellers' }
    ];

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info"
                                display="flex" justifyContent="space-between" alignItems="center"
                            >
                                <MDBox>
                                    <MDTypography variant="h5" color="white">{component.name}</MDTypography>
                                    <MDTypography variant="button" color="white" opacity={0.8}>Trip Logistics Details</MDTypography>
                                </MDBox>
                                <MDBox display="flex" gap={1}>
                                    {isEditing ? (
                                        <>
                                            <IconButton onClick={() => { setIsEditing(false); setUpdatedFields({}); fetchData(); }} sx={commonIconButtonStyles}><Close sx={commonIconStyles} /></IconButton>
                                            <IconButton onClick={handleSaveChanges} sx={commonIconButtonStyles}><Save sx={commonIconStyles} /></IconButton>
                                        </>
                                    ) : (
                                        <IconButton onClick={() => setIsEditing(true)} sx={commonIconButtonStyles}><Edit sx={commonIconStyles} /></IconButton>
                                    )}
                                    <IconButton onClick={handleDelete} sx={commonIconButtonStyles}><Delete sx={commonIconStyles} /></IconButton>
                                </MDBox>
                            </MDBox>
                            <MDBox p={3}>
                                {renderSection("Basic Information", [
                                    { label: "Name", field: "name" },
                                    { label: "Category", field: "category", options: categoryOptions },
                                    { label: "Sub Category", field: "sub_category", options: accomSubCategories }, // Note in edit mode this should be conditionally rendered/enabled, inline edit simplifies this
                                    { label: "Trip ID", field: "trip_id" },
                                    { label: "Trip Option ID", field: "trip_option_id" },
                                    { label: "Status", field: "status", options: [{ value: "Active", label: "Active" }, { value: "Inactive", label: "Inactive" }] },
                                ])}

                                {renderSection("Pricing & Details", [
                                    { label: "List Price", field: "price", type: "number" },
                                    { label: "Net Cost", field: "net_cost", type: "number" },
                                    { label: "Departure City", field: "departure_city", fullWidth: true, multiline: true },
                                    { label: "Description", field: "description", fullWidth: true, multiline: true },
                                ])}

                                {renderSection("Audit Information", [
                                    { label: "Created By", field: "created_by_name" },
                                    { label: "Modified By", field: "updated_by_name" },
                                    { label: "Created Date", field: "created_at", readOnly: true, renderValue: (val) => formatDate(val, 'datetime') },
                                    { label: "Modified Date", field: "updated_at", readOnly: true, renderValue: (val) => formatDate(val, 'datetime') },
                                ])}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            
            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default TripComponentDetail;
